/**
Author - Venkatesh Pullaganti 😃
Git - https://github.com/venkatesh1146
**/

import React, {createContext, useContext, useState, useEffect, useMemo} from 'react';
import {ThemeProvider as StyledThemeProvider} from 'styled-components';
import {clientTheme} from './ClientTheme';

import {partnerTheme} from './PartnerTheme';

import {PartnerGlobalStyles} from '../static/PartnerGlobalStyles';
import {ClientGlobalStyles} from '../static/ClientGlobalStyles';
import {mergeWhiteLabelTheme} from './mergeWhiteLabelTheme';

export const KYCThemeContext = createContext({
  currentTheme: 'client',
  setTheme: (theme) => {},
  themes: {client: clientTheme, partner: partnerTheme},
});

export const useTheme = () => useContext(KYCThemeContext);

/**
 * ThemeProvider component that provides theme context to its children.
 *
 * @param {Object} props - The properties object.
 * @param {React.ReactNode} props.children - The child components.
 * @param {'client' | 'partner'} [props.initialTheme='client'] - The initial theme to be set.
 * @param {Object|null} [props.whiteLabelConfig] - Fetched white-label config to overlay on the base themes.
 * @returns {JSX.Element} The ThemeProvider component.
 */
export const ThemeProvider = ({children, initialTheme = 'client', whiteLabelConfig = null}) => {
  const themes = useMemo(() => {
    return {
      client: mergeWhiteLabelTheme(clientTheme, whiteLabelConfig),
      partner: mergeWhiteLabelTheme(partnerTheme, whiteLabelConfig),
    };
  }, [whiteLabelConfig]);

  const [currentTheme, setCurrentTheme] = useState(initialTheme);

  useEffect(() => {
    setCurrentTheme(initialTheme);
  }, [initialTheme]);

  const setTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    } else {
      console.warn(`Theme "${themeName}" not found.`);
    }
  };

  const themeContextValue = {
    currentTheme,
    setTheme,
    themes,
  };

  return (
    <KYCThemeContext.Provider value={themeContextValue}>
      <StyledThemeProvider theme={themes[currentTheme]}>
        {currentTheme === 'client' ? <ClientGlobalStyles /> : <PartnerGlobalStyles />}
        {children}
      </StyledThemeProvider>
    </KYCThemeContext.Provider>
  );
};
